/**
 * Name Formatting Utility - Global Pipe
 * Formats names according to country-specific conventions
 */

export interface NameParts {
  firstName: string;
  lastName: string;
  middleName?: string;
}

export type CountryCode =
  | 'USA' | 'UK' | 'CAN' | 'AUS'  // Western
  | 'IND'                          // Indian
  | 'CHN' | 'JPN' | 'KOR' | 'VNM' // East Asian
  | 'ESP' | 'MEX' | 'ARG' | 'COL' // Spanish/Hispanic
  | 'BRA' | 'PRT'                  // Portuguese
  | 'RUS'                          // Russian
  | 'ARE' | 'SAU' | 'EGY'         // Arabic
  | 'DEU' | 'FRA' | 'ITA'         // European
  | 'IDN' | 'THA'                  // Southeast Asian
  | 'DEFAULT';                     // Fallback

/**
 * Format name based on country code
 * @param firstName - Given/First name
 * @param lastName - Family/Last name/Surname
 * @param middleName - Middle name (optional)
 * @param countryCode - Country code for formatting
 * @returns Formatted full name
 */
export function formatNameByCountry(
  firstName: string,
  lastName: string,
  middleName: string = '',
  countryCode: CountryCode = 'DEFAULT'
): string {
  // Clean and validate inputs
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  const middle = (middleName || '').trim();

  // Handle empty names
  if (!first && !last && !middle) {
    return '-';
  }

  // Single name fallback
  if (!last && !middle) {
    return first;
  }
  if (!first && !middle) {
    return last;
  }

  // Format based on country code
  switch (countryCode) {
    // Western Format: First Middle Last
    case 'USA':
    case 'UK':
    case 'CAN':
    case 'AUS':
    case 'DEFAULT':
      return formatWestern(first, last, middle);

    // Indian Format: First Middle Last
    case 'IND':
      return formatIndian(first, last, middle);

    // East Asian Format: Last First (reversed)
    case 'CHN':
    case 'JPN':
    case 'KOR':
    case 'VNM':
      return formatEastAsian(first, last, middle);

    // Spanish/Hispanic Format: First Last MiddleLast
    case 'ESP':
    case 'MEX':
    case 'ARG':
    case 'COL':
      return formatSpanish(first, last, middle);

    // Portuguese/Brazilian Format: First Middle Last
    case 'BRA':
    case 'PRT':
      return formatPortuguese(first, last, middle);

    // Russian Format: First Middle Last
    case 'RUS':
      return formatRussian(first, last, middle);

    // Arabic Format: First Middle Last
    case 'ARE':
    case 'SAU':
    case 'EGY':
      return formatArabic(first, last, middle);

    // European Format: First Middle Last
    case 'DEU':
    case 'FRA':
    case 'ITA':
      return formatEuropean(first, last, middle);

    // Southeast Asian Format: First Last
    case 'IDN':
    case 'THA':
      return formatSoutheastAsian(first, last, middle);

    default:
      return formatWestern(first, last, middle);
  }
}

/**
 * Western Format: First Middle Last
 * Example: John Michael Smith
 */
function formatWestern(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * Indian Format: First Middle Last
 * Example: Rajesh Kumar Sharma
 */
function formatIndian(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * East Asian Format: Last First (Family name first)
 * Example: Wang Wei (not Wei Wang)
 */
function formatEastAsian(first: string, last: string, middle: string): string {
  if (middle) {
    // Middle name comes after last in East Asian format
    return `${last} ${first} ${middle}`;
  }
  return `${last} ${first}`;
}

/**
 * Spanish/Hispanic Format: First PaternalLast MaternalLast
 * Example: María García Rodríguez
 * Note: Middle name treated as maternal surname
 */
function formatSpanish(first: string, last: string, middle: string): string {
  if (middle) {
    // Middle is maternal surname
    return `${first} ${last} ${middle}`;
  }
  return `${first} ${last}`;
}

/**
 * Portuguese/Brazilian Format: First Middle Last
 * Example: João Pedro Silva Santos
 */
function formatPortuguese(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * Russian Format: First Middle Last
 * Example: Ivan Petrovich Sidorov
 * Note: Middle name is patronymic
 */
function formatRussian(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * Arabic Format: First Middle Last
 * Example: Ahmed Mohammed Al-Masri
 */
function formatArabic(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * European Format: First Middle Last
 * Example: Jean Claude Dubois
 */
function formatEuropean(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * Southeast Asian Format: First Last
 * Example: Somchai Wongsawat
 * Note: Middle names less common
 */
function formatSoutheastAsian(first: string, last: string, middle: string): string {
  if (middle) {
    return `${first} ${middle} ${last}`;
  }
  return `${first} ${last}`;
}

/**
 * Get initials from name
 * @param firstName - First name
 * @param lastName - Last name
 * @param middleName - Middle name (optional)
 * @returns Initials (e.g., "JMS" for John Michael Smith)
 */
export function getNameInitials(
  firstName: string,
  lastName: string,
  middleName: string = ''
): string {
  const first = (firstName || '').trim().charAt(0).toUpperCase();
  const last = (lastName || '').trim().charAt(0).toUpperCase();
  const middle = (middleName || '').trim().charAt(0).toUpperCase();

  if (first && middle && last) {
    return `${first}${middle}${last}`;
  }
  if (first && last) {
    return `${first}${last}`;
  }
  return first || last || '?';
}

/**
 * Format name for sorting (Last, First Middle)
 * @param firstName - First name
 * @param lastName - Last name
 * @param middleName - Middle name (optional)
 * @returns Sort-friendly format
 */
export function formatNameForSort(
  firstName: string,
  lastName: string,
  middleName: string = ''
): string {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  const middle = (middleName || '').trim();

  if (!first && !last && !middle) {
    return '-';
  }

  if (middle) {
    return `${last}, ${first} ${middle}`;
  }
  return `${last}, ${first}`;
}

/**
 * Get display name (First Last only, no middle)
 * @param firstName - First name
 * @param lastName - Last name
 * @returns Short display name
 */
export function getDisplayName(firstName: string, lastName: string): string {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();

  if (!first && !last) {
    return '-';
  }
  if (!last) {
    return first;
  }
  if (!first) {
    return last;
  }

  return `${first} ${last}`;
}

/**
 * Format name with title
 * @param firstName - First name
 * @param lastName - Last name
 * @param title - Title (Mr., Dr., Ms., etc.)
 * @returns Formatted name with title
 */
export function formatNameWithTitle(
  firstName: string,
  lastName: string,
  title: string = ''
): string {
  const name = getDisplayName(firstName, lastName);
  if (title) {
    return `${title} ${name}`;
  }
  return name;
}

/**
 * Parse full name into parts (best effort)
 * @param fullName - Full name string
 * @returns Name parts object
 */
export function parseFullName(fullName: string): NameParts {
  const parts = (fullName || '').trim().split(/\s+/);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  if (parts.length === 2) {
    return { firstName: parts[0], lastName: parts[1] };
  }

  // 3+ parts: First, Middle(s), Last
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

/**
 * Get country name from code
 * @param countryCode - Country code
 * @returns Country name
 */
export function getCountryName(countryCode: CountryCode): string {
  const countryNames: Record<CountryCode, string> = {
    USA: 'United States',
    UK: 'United Kingdom',
    CAN: 'Canada',
    AUS: 'Australia',
    IND: 'India',
    CHN: 'China',
    JPN: 'Japan',
    KOR: 'South Korea',
    VNM: 'Vietnam',
    ESP: 'Spain',
    MEX: 'Mexico',
    ARG: 'Argentina',
    COL: 'Colombia',
    BRA: 'Brazil',
    PRT: 'Portugal',
    RUS: 'Russia',
    ARE: 'United Arab Emirates',
    SAU: 'Saudi Arabia',
    EGY: 'Egypt',
    DEU: 'Germany',
    FRA: 'France',
    ITA: 'Italy',
    IDN: 'Indonesia',
    THA: 'Thailand',
    DEFAULT: 'Default',
  };

  return countryNames[countryCode] || 'Unknown';
}

// Export default as the main formatting function
export default formatNameByCountry;

